import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { getAnnualWeekWidths } from "../../lib";

/**
 * Measures the matrix container and keeps week widths in sync with its available space.
 *
 * @param weeks - Visible weeks; filtering recomputes widths without another network request.
 * @returns The container ref and widths shared by the matrix and skeleton.
 *
 * @remarks Observes the container rather than the table, whose minimum width can overflow.
 * Disconnects the observer on unmount, including transitions between loading and results.
 */
export const useAnnualScheduleLayout = (weeks: readonly { name: string }[]) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    setContainerWidth(container.getBoundingClientRect().width);

    const observer = new ResizeObserver(([entry]) => {
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  const weekWidths = useMemo(
    () => getAnnualWeekWidths(weeks, containerWidth),
    [weeks, containerWidth],
  );

  return { containerRef, weekWidths };
};
